import { Directive, ElementRef, HostListener, OnInit, AfterViewChecked,Input } from '@angular/core';
import * as Hammer from 'hammerjs';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneTools from 'cornerstone-tools';
import * as cornerstoneMath from 'cornerstone-math';



@Directive({
  selector: '[cornerstone]',
})
export class CornerstoneDirective implements OnInit, AfterViewChecked {
  public element: any;
  public imageList = [];
  private imageIdList = [];
  public currentIndex = 0;
  public currentImage: any;
  public patientName = '';
  public hospital = '';
  public instanceNumber = '';
  @Input() crosshairModeEnabled: boolean = false;
  private crosshairMouseDownHandler: any;
  private crosshairMouseMoveHandler: any;
  public stackScrollWheelEnabled: boolean = false; 
  private stackScrollWheelHandler: any;



  @Input() filename!: string;
  @Input() fileType!: string;
  @Input() onIndexChange?: (index: number) => void;


  private ZoomTouchPinchTool = cornerstoneTools.ZoomTouchPinchTool;
  private PanMultiTouchTool = cornerstoneTools.PanMultiTouchTool;

  private isCornerstoneEnabled = false;
  private CrosshairsTool = cornerstoneTools.CrosshairsTool;

  

  public get windowingValue(): string {
    if (this.isCornerstoneEnabled) {
      let viewport = cornerstone.getViewport(this.element);
      if (this.currentImage && viewport) { return Math.round(viewport.voi.windowWidth) + "/" + Math.round(viewport.voi.windowCenter); }
    }
    return '';
  }


  public resetViewer() {
  this.disableViewer();
  cornerstone.enable(this.element);
  setTimeout(() => {
    this.isCornerstoneEnabled = true;
  }, 100);
}

public disableViewer() {
  this.element = this.elementRef.nativeElement;
  try {
    cornerstone.disable(this.element);
  } finally {}
  this.isCornerstoneEnabled = false;
}


public enableFlipH() {
  if (this.isCornerstoneEnabled) {
    const viewport = cornerstone.getViewport(this.element);
    viewport.hflip = !viewport.hflip;
    cornerstone.setViewport(this.element, viewport);
  }
}

// Flip Vertical
public enableFlipV() {
  if (this.isCornerstoneEnabled) {
    const viewport = cornerstone.getViewport(this.element);
    viewport.vflip = !viewport.vflip;
    cornerstone.setViewport(this.element, viewport);
  }
}

public enableCrosshairs(): boolean {
  if (!this.isCornerstoneEnabled || this.imageList.length < 2) {
    return false;
  }

  try {
    // Clear any existing state
    cornerstoneTools.clearToolState(this.element, 'Crosshairs');
    
    // Set up stack state first
    const stack = {
      currentImageIdIndex: this.currentIndex,
      imageIds: this.imageIdList
    };
    
    cornerstoneTools.addStackStateManager(this.element, ['stack']);
    cornerstoneTools.addToolState(this.element, 'stack', stack);

    // Set up crosshair tool
    cornerstoneTools.setToolActiveForElement(
      this.element, 
      'Crosshairs', 
      { 
        mouseButtonMask: 1,
        configuration: {
          viewportIndicators: true,
          autoPan: {
            enabled: true,
            panSize: 10
          },
          syncZoom: true,
          syncPan: true,
          syncStackScroll: true
        }
      }, 
      ['Mouse']
    );
    
    return true;
  } catch (error) {
    // console.error('Error enabling crosshairs:', error);
    return false;
  }
}

 getElement(): HTMLDivElement {
    return this.el.nativeElement;
  }

public addAllImagesForCrosshairs(images: any[]) {
  this.resetImageCache();
  this.imageList = [...images];
  this.imageIdList = images.map(img => img.imageId);
  
  if (images.length > 0) {
    this.currentIndex = 0;
    
    // Create stack for crosshairs
    const stack = {
      currentImageIdIndex: 0,
      imageIds: this.imageIdList,
      frameRate: 15
    };
    
    cornerstoneTools.addStackStateManager(this.element, ['stack']);
    cornerstoneTools.addToolState(this.element, 'stack', stack);
    
    // Display first image
    const viewport = cornerstone.getDefaultViewportForImage(this.element, images[0]);
    cornerstone.displayImage(this.element, images[0], viewport);
    
    // Enable crosshairs if multiple images
    if (images.length > 1) {
      this.enableCrosshairs();
    }
  }
}

public disableCrosshairs() {
  try {
    // Remove event listeners if they exist
    if (this.crosshairMouseDownHandler) {
      this.element.removeEventListener('cornerstonetoolsmousedown', this.crosshairMouseDownHandler);
      this.crosshairMouseDownHandler = null;
    }
    if (this.crosshairMouseMoveHandler) {
      this.element.removeEventListener('cornerstonetoolsmousemove', this.crosshairMouseMoveHandler);
      this.crosshairMouseMoveHandler = null;
    }

    // Disable the tool
    cornerstoneTools.setToolDisabledForElement(this.element, 'Crosshairs');
    
    // Clear tool state
    cornerstoneTools.clearToolState(this.element, 'Crosshairs');
    
    // Force a redraw
    cornerstone.updateImage(this.element);
  } catch (error) {
    // console.error('Error disabling crosshairs:', error);
  }
}

public enableScaleOverlay() {
  if (!this.isCornerstoneEnabled) return;
  
  // Get current image
  const image = cornerstone.getImage(this.element);
  
  // If pixel spacing is missing, add default values
  if (image && !image.rowPixelSpacing && !image.columnPixelSpacing) {
    image.rowPixelSpacing = 1;
    image.columnPixelSpacing = 1;
    cornerstone.updateImage(this.element);
  }
  
  // Activate the tool
  cornerstoneTools.setToolActiveForElement(this.element, 'ScaleOverlay', { 
    mouseButtonMask: 1 
  }, ['Mouse']);
}


  public get zoomValue(): string {
    if (this.isCornerstoneEnabled) {
      let viewport = cornerstone.getViewport(this.element);
      if (this.currentImage && viewport) { return viewport.scale.toFixed(2); }
    }
    return '';
  }



  constructor(private elementRef: ElementRef, private el: ElementRef) {}

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (this.isCornerstoneEnabled) {
      cornerstone.resize(this.element, true);
    }
  }
  

@HostListener('wheel', ['$event'])
onMouseWheel(event: WheelEvent) {
  // Only allow wheel events if stack scroll is enabled
  if (!this.stackScrollWheelEnabled) {
    event.preventDefault();
    return;
  }

  if (this.imageList.length > 0) {
    const delta = Math.max(-1, Math.min(1, (event.deltaY || -event.detail)));
    let newIndex = this.currentIndex;
    
    if (delta > 0) {
      newIndex++;
      if (newIndex >= this.imageList.length) {
        newIndex = this.imageList.length - 1;
      }
    } else {
      newIndex--;
      if (newIndex < 0) {
        newIndex = 0;
      }
    }
    
    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
      this.displayImage(this.imageList[this.currentIndex]);
      
      // Call the parent callback if provided
      if (this.onIndexChange) {
        this.onIndexChange(this.currentIndex);
      }
    }
  }
}


ngOnInit() {
  this.element = this.elementRef.nativeElement;
  
  // Initialize Cornerstone Tools
  cornerstoneTools.external.cornerstone = cornerstone;
  cornerstoneTools.external.Hammer = Hammer;
  cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
  cornerstoneTools.init({ 
    globalToolSyncEnabled: true,
    showSVGCursors: true
  });
  // cornerstoneTools.init();

  // Configure color LUT
  const colorLUT = Array.from({ length: 65536 }, (_, i) => [
    i % 256, i % 256, i % 256, 255
  ]);
  cornerstoneTools.store.state.colorLUTs = { 0: colorLUT };

  // Register all tools with consistent naming
  cornerstoneTools.addTool(cornerstoneTools.WwwcTool);
  cornerstoneTools.addTool(cornerstoneTools.PanTool);
  cornerstoneTools.addTool(cornerstoneTools.ZoomTool, {
    configuration: {
      invert: false,
      preventZoomOutsideImage: false,
      minScale: 0.1,
      maxScale: 20.0
    }
  });

  cornerstoneTools.addTool(cornerstoneTools.StackScrollMouseWheelTool);

  cornerstoneTools.addTool(this.CrosshairsTool, {
  configuration: {
    viewportIndicators: true,
    autoPan: {
      enabled: false,
      panSize: 10
    }
  }
});
  
  // Measurement tools
  cornerstoneTools.addTool(cornerstoneTools.LengthTool);
  cornerstoneTools.addTool(cornerstoneTools.AngleTool);
  cornerstoneTools.addTool(this.ZoomTouchPinchTool);
  cornerstoneTools.addTool(this.PanMultiTouchTool);

  cornerstoneTools.addTool(cornerstoneTools.ArrowAnnotateTool);
  
  cornerstoneTools.addTool(cornerstoneTools.CobbAngleTool);
  cornerstoneTools.addTool(cornerstoneTools.EllipticalRoiTool);
  cornerstoneTools.addTool(cornerstoneTools.RectangleRoiTool);
  cornerstoneTools.addTool(cornerstoneTools.FreehandRoiTool);

  const MagnifyTool = cornerstoneTools.MagnifyTool;
  cornerstoneTools.addTool(MagnifyTool)
  cornerstoneTools.addTool(cornerstoneTools.ScaleOverlayTool, {
  configuration: {
    font: '16px Arial',
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 5,
    // Add these configurations:
    defaultPixelSpacing: 1, // Default to 1mm if spacing not found
    showPixelSpacing: true, // Show actual spacing if available
    showScale: true // Always show scale even if spacing missing
  }
});
  cornerstoneTools.addTool(cornerstoneTools.FreehandRoiTool);
  const TextMarkerTool = cornerstoneTools.TextMarkerTool
  const configuration = {
    markers: ['F5', 'F4', 'F3', 'F2', 'F1'],
    current: 'F5',
    ascending: true,
    loop: true,
  }

  // Other tools
  cornerstoneTools.addTool(TextMarkerTool, { configuration });
  cornerstoneTools.addTool(cornerstoneTools.ProbeTool);
  cornerstoneTools.addTool(cornerstoneTools.DragProbeTool);
  cornerstoneTools.addTool(cornerstoneTools.StackScrollTool);
  cornerstoneTools.addTool(cornerstoneTools.StackScrollMouseWheelTool);
  cornerstoneTools.addTool(cornerstoneTools.RotateTool);
  // cornerstoneTools.addTool(cornerstoneTools.FlipHTool);
  // cornerstoneTools.addTool(cornerstoneTools.FlipVTool);

  cornerstoneTools.addTool(cornerstoneTools.EraserTool, {
  configuration: {
    // Can erase all these tool types
    toolTypes: [
      'Length', 'Angle', 'Probe', 'EllipticalRoi', 'RectangleRoi',
      'CobbAngle', 'FreehandRoi', 'ArrowAnnotate', 'TextMarker'
    ]
  }
});

  this.logRegisteredTools();
  this.resetViewer();
}




public syncWithViewer(images: any[]) {
  this.resetImageCache();
  this.imageList = [...images];
  this.imageIdList = images.map(img => img.imageId);
  
  if (images.length > 0) {
    // Start at the current index, not always 0
    this.currentIndex = Math.min(this.currentIndex, images.length - 1);
    this.displayImage(images[this.currentIndex]);
  }
}


public enableStackScrollWheel(): boolean {
  try {
    // First disable any existing instance
    this.disableStackScrollWheelCompletely();
    
    // Activate the tool
    cornerstoneTools.setToolActiveForElement(
      this.element,
      'StackScrollMouseWheel',
      {},
      ['Mouse']
    );
    
    this.stackScrollWheelEnabled = true;
    return true;
  } catch (error) {
    // console.error('Error enabling stack scroll wheel:', error);
    this.stackScrollWheelEnabled = false;
    return false;
  }
}

public disableStackScrollWheelCompletely() {
  try {
    // Remove the tool from the element
    cornerstoneTools.setToolDisabledForElement(this.element, 'StackScrollMouseWheel');
    
    // Remove any existing mouse wheel event listeners
    if (this.stackScrollWheelHandler) {
      this.element.removeEventListener('wheel', this.stackScrollWheelHandler);
      this.stackScrollWheelHandler = null;
    }
    
    this.stackScrollWheelEnabled = false;
    cornerstone.updateImage(this.element);
  } catch (error) {
    // console.error('Error disabling stack scroll wheel:', error);
  }
}

public resetAllCursors() {
  if (this.element) {
    const canvases = this.element.querySelectorAll('canvas');
    canvases.forEach(canvas => {
      canvas.style.cursor = 'default';
    });
  }
}

public logRegisteredTools() {
  
  const toolNames = cornerstoneTools.store.state.tools;
  
  const syncManager = cornerstoneTools.globalToolSyncManager;
  if (syncManager) {
    // console.log('Sync Manager Tools:');
    // console.log(syncManager.tools);
  }
}
  ngAfterViewChecked() {}


  public resetImageCache() {
    this.imageList = [];
    this.imageIdList = [];
    this.currentImage = null;
    this.currentIndex = 0;
    this.patientName = '';
    this.hospital = '';
    this.instanceNumber = '';
  }


  public previousImage() {
    if (this.imageList.length > 0) {
      this.currentIndex--;
      if (this.currentIndex < 0) {
        this.currentIndex = 0;
      }
      this.displayImage(this.imageList[this.currentIndex]);
    }
  }

  public nextImage() {
    if (this.imageList.length > 0) {
      this.currentIndex++;
      if (this.currentIndex >= this.imageList.length) {
        this.currentIndex = this.imageList.length - 1;
      }
      this.displayImage(this.imageList[this.currentIndex]);
    }
  }


public displayImage(image) {
  this.element = this.elementRef.nativeElement;
  
  if (!image.rowPixelSpacing && !image.columnPixelSpacing) {
    image.rowPixelSpacing = 1;
    image.columnPixelSpacing = 1;
  }

  const viewport = cornerstone.getDefaultViewportForImage(this.element, image);
  cornerstone.displayImage(this.element, image, viewport);
  this.currentImage = image;
  
  // Update stack state
  const stack = {
    currentImageIdIndex: this.currentIndex,
    imageIds: this.imageIdList
  };
  
  cornerstoneTools.addStackStateManager(this.element, ['stack', 'playClip']);
  cornerstoneTools.addToolState(this.element, 'stack', stack);
  
  cornerstone.fitToWindow(this.element);
  cornerstone.resize(this.element, true);
}

public addImageData(imageData: any) {
  if (this.imageList.length > 0 && this.crosshairModeEnabled) {
    return;
  }

  this.element = this.elementRef.nativeElement;
  this.imageList.push(imageData);
  this.imageIdList.push(imageData.imageId);
  
  // Update stack state
  const stack = {
    currentImageIdIndex: this.currentIndex,
    imageIds: this.imageIdList
  };
  
  cornerstoneTools.addStackStateManager(this.element, ['stack', 'playClip']);
  cornerstoneTools.addToolState(this.element, 'stack', stack);
  
  if (this.imageList.length === 1) {
    this.currentIndex = 0;
    this.displayImage(imageData);
  }
  
  cornerstone.resize(this.element, true);
}


  public resetAllTools() {
    cornerstoneTools.setToolDisabledForElement(this.element, 'Wwwc');
    cornerstoneTools.setToolDisabledForElement(this.element, 'Pan');
    cornerstoneTools.setToolDisabledForElement(this.element, 'Zoom');
    cornerstoneTools.setToolDisabledForElement(this.element, 'Probe');
    cornerstoneTools.setToolDisabledForElement(this.element, 'Length');
    cornerstoneTools.setToolDisabledForElement(this.element, 'Angle');
    cornerstoneTools.setToolDisabledForElement(this.element, 'EllipticalRoi');
    cornerstoneTools.setToolDisabledForElement(this.element, 'RectangleRoi');
    cornerstoneTools.setToolDisabledForElement(this.element, 'DragProbe');
    cornerstoneTools.setToolDisabledForElement(this.element, 'ZoomTouchPinch');
    cornerstoneTools.setToolDisabledForElement(this.element, 'PanMultiTouch');
    cornerstoneTools.setToolDisabledForElement(this.element, 'StackScroll');
    cornerstoneTools.setToolDisabledForElement(this.element, 'StackScrollMouseWheel');
    cornerstoneTools.setToolDisabledForElement(this.element, 'Rotate');

    cornerstoneTools.setToolDisabledForElement(this.element, 'Bidirectional');
    cornerstoneTools.setToolDisabledForElement(this.element, 'CobbAngle');
    cornerstoneTools.setToolDisabledForElement(this.element, 'FreehandRoi');
    cornerstoneTools.setToolDisabledForElement(this.element, 'ArrowAnnotate');
    cornerstoneTools.setToolDisabledForElement(this.element, 'TextMarker');
    cornerstoneTools.setToolDisabledForElement(this.element, 'ScaleOverlayTool');
    
  }

  public deactivateAllTools() {
  const toolsToDeactivate = [
    'Wwwc', 'Pan', 'Zoom', 'Probe', 'Length', 'Angle','Magnify','ScaleOverlayTool',
    'EllipticalRoi', 'RectangleRoi', 'DragProbe', 'ZoomTouchPinch','CrosshairsTool',
    'PanMultiTouch', 'StackScroll', 'StackScrollMouseWheel', 'Rotate','CobbAngle', 'FreehandRoi', 'ArrowAnnotate', 'TextMarker'
  ];

  toolsToDeactivate.forEach(toolName => {
    try {
      cornerstoneTools.setToolDisabledForElement(this.element, toolName);
    } catch (error) {
      // console.warn(`Error disabling tool ${toolName}:`, error);
    }
  });
}

}